/**
 * Events — shared helpers for the phone and desktop builds.
 *
 * Every date here is read in Malaysia time (UTC+8), whatever the device clock's
 * zone says: an event "on Thursday at 7 PM" is HQ's Thursday, and an agent on a
 * phone still set to Singapore or travelling must not see it slide a day.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RM } from './format';

dayjs.extend(utc);

const MY_OFFSET = 8 * 60;

/** A dayjs in Malaysia time. No argument = now. */
export const my = (v) => (v === undefined ? dayjs() : dayjs(v)).utcOffset(MY_OFFSET);

/** Monday 00:00 (Malaysia) of the week holding `d`. */
export const weekStart = (d) => {
  const day = my(d).startOf('day');
  const dow = (day.day() + 6) % 7; // Mon = 0
  return day.subtract(dow, 'day');
};

/** First day of the month holding `d`, Malaysia time. */
export const monthStart = (d) => my(d).startOf('day').date(1);

/** YYYY-MM-DD of an instant, in Malaysia time — the key events are grouped by. */
export const dayKey = (v) => my(v).format('YYYY-MM-DD');

/** "7:00" + "PM" for the time column. */
export const timeParts = (iso) => {
  const d = my(iso);
  return { hm: d.format('h:mm'), ampm: d.format('A') };
};

/** "7:00 – 11:00 PM", or "10:00 AM – 4:00 PM", or across days "Thu 8 Oct 7:00 PM – Fri 9 Oct 1:00 AM". */
export const timeRange = (startIso, endIso) => {
  const s = my(startIso);
  const e = my(endIso);
  if (s.format('YYYY-MM-DD') !== e.format('YYYY-MM-DD')) {
    return `${s.format('ddd D MMM, h:mm A')} – ${e.format('ddd D MMM, h:mm A')}`;
  }
  if (s.format('A') === e.format('A')) return `${s.format('h:mm')} – ${e.format('h:mm A')}`;
  return `${s.format('h:mm A')} – ${e.format('h:mm A')}`;
};

/** "Thu, 8 Oct 2026 · 7:00 – 11:00 PM" */
export const whenLong = (ev) => {
  const s = my(ev.starts_at);
  const e = my(ev.ends_at);
  if (s.format('YYYY-MM-DD') !== e.format('YYYY-MM-DD')) return timeRange(ev.starts_at, ev.ends_at);
  return `${s.format('ddd, D MMM YYYY')} · ${timeRange(ev.starts_at, ev.ends_at)}`;
};

/** "Thu, 8 Oct · 7:00 PM" */
export const whenShort = (iso) => my(iso).format('ddd, D MMM · h:mm A');

/** "7:05 PM" */
export const clock = (iso) => my(iso).format('h:mm A');

/** A calendar block's time: the range when it starts and ends the same day, else "7:00 PM →". */
export const blockTime = (startIso, endIso) => (dayKey(startIso) === dayKey(endIso)
  ? timeRange(startIso, endIso)
  : `${my(startIso).format('h:mm A')} →`);

/** "Thu 8 Oct" day header, with "Today" / "Tomorrow" called out. */
export const dayHeader = (key) => {
  const d = my(`${key}T12:00:00+08:00`);
  const today = dayKey();
  const tomorrow = dayKey(my().add(1, 'day'));
  const base = d.format('ddd, D MMM');
  if (key === today) return `${base} · Today`;
  if (key === tomorrow) return `${base} · Tomorrow`;
  return base;
};

// ── colour ───────────────────────────────────────────────────────────────────

const hexRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  const n = m ? parseInt(m[1], 16) : 0x18609c;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** The type's colour at `alpha` — a tint that works on light and dark surfaces. */
export const tint = (hex, alpha = 0.12) => {
  const [r, g, b] = hexRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** A darker shade of the type colour for text on a tint. */
export const shade = (hex, k = 0.72) => {
  const [r, g, b] = hexRgb(hex);
  const f = (c) => Math.round(c * k).toString(16).padStart(2, '0');
  return `#${f(r)}${f(g)}${f(b)}`;
};

/**
 * Type-coloured TEXT on the app's own surfaces. The raw type colour is a mid
 * tone: fine as a fill, but as text it falls under 2:1 on the dark theme's
 * surface. Mixing it with --text darkens it on the light theme and lightens it
 * on the dark one, so it stays recognisably "the blue one" and readable (≥ 4.5:1
 * for the four seeded colours) in both.
 */
export const typeText = (hex) => `color-mix(in srgb, ${hex} 58%, var(--text))`;

/** White or near-black, whichever reads better ON the type colour (HQ can pick any colour). */
export const onColor = (hex) => {
  const lin = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  const [r, g, b] = hexRgb(hex);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return 1.05 / (L + 0.05) >= (L + 0.05) / 0.05 ? '#FFFFFF' : '#141A24';
};

export const typeColor = (ev) => ev?.event_type?.color || '#18609C';
export const typeName = (ev) => ev?.event_type?.name || 'Event';

// ── images ──────────────────────────────────────────────────────────────────

const VIDEO_URL_RE = /\.(mp4|mov|webm)(\?|#|$)/i;

/**
 * HQ's poster first, then the gallery — the order the viewer pages through.
 * The list endpoint sends `poster_url` only; `gallery` comes with the detail.
 * A gallery item may be a video (`video: true`): the server's `kind`, else the
 * file extension. The poster is always an image.
 */
export const eventImages = (ev) => [
  ev?.poster_url ? { url: ev.poster_url, kind: 'poster' } : null,
  ...(Array.isArray(ev?.gallery) ? ev.gallery : [])
    .filter((g) => g?.url)
    .map((g) => ({ url: g.url, kind: 'photo', video: g.kind === 'video' || VIDEO_URL_RE.test(g.url) })),
].filter(Boolean);

// ── words ───────────────────────────────────────────────────────────────────

export const modeWords = (ev) => {
  switch (ev.registration_mode) {
    case 'NONE': return 'No registration — just turn up';
    case 'ONE_CLICK': return 'One-tap registration';
    case 'FORM': return 'Registration form';
    case 'PAID': return `Paid · ${RM(ev.fee_per_pax)} per pax`;
    default: return '';
  }
};

export const PAYMENT_WORDS = {
  NOT_REQUIRED: 'No payment',
  PENDING: 'Payment pending',
  PAID: 'Paid',
  COMPLIMENTARY: 'Complimentary',
  REFUNDED: 'Refunded',
};

const hasQr = (ev) => ['EVENT_QR', 'BOTH'].includes(ev?.checkin_mode);
export const canSelfCheckIn = (ev) => hasQr(ev) && ev?.status === 'PUBLISHED' && ev?.checkin_window?.state !== 'CLOSED';
export const selfCheckInOpen = (ev) => hasQr(ev) && ev?.status === 'PUBLISHED' && ev?.checkin_window?.state === 'OPEN';

/** Registered, not yet in, and HQ still waiting on the money: the desk checks them in, not the event QR. */
export const owesAtDoor = (ev) => {
  const r = ev?.my_registration;
  return !!r && r.status === 'REGISTERED' && !r.checked_in_at && r.payment_status === 'PENDING';
};

/**
 * Whether "Scan event QR" can work for this agent right now — the same rules the
 * server applies, so the button never leads to a refusal: the event QR is on and
 * the window is open, and either they hold a paid-up (or free) seat and are not
 * in yet, or they hold nothing on an event that takes walk-ins (a paid event
 * sends walk-ins to the desk; the waitlist goes to the desk too).
 */
export const canScanNow = (ev) => {
  if (!selfCheckInOpen(ev)) return false;
  const r = ev.my_registration;
  if (r && r.status === 'REGISTERED') return !r.checked_in_at && r.payment_status !== 'PENDING';
  if (r && r.status === 'WAITLISTED') return false;
  return ['NONE', 'ONE_CLICK', 'FORM'].includes(ev.registration_mode);
};

/**
 * The most pax this agent can ask for: the event's per-agent limit, and — when a
 * full event turns people away instead of waitlisting them — the seats left.
 */
export const paxCap = (ev) => {
  const max = Math.max(1, Number(ev?.max_pax_per_agent) || 1);
  if (!ev?.waitlist_enabled && ev?.seats_left != null) return Math.max(1, Math.min(max, ev.seats_left));
  return max;
};

/**
 * The one status an agent needs to read off a calendar card, and the chip tone
 * for it. Mine first (registered / waitlisted / checked in / payment), then the
 * event's own (cancelled / closed / full / open).
 */
export const myStatus = (ev) => {
  const r = ev.my_registration;
  if (ev.status === 'CANCELLED') return { label: 'Cancelled', tone: 'bad' };
  if (r && r.status === 'REGISTERED') {
    if (r.checked_in_at) return { label: 'Checked in', tone: 'ok' };
    if (r.payment_status === 'PENDING') return { label: 'Payment pending', tone: 'warn' };
    return { label: 'Registered', tone: 'ok' };
  }
  if (r && r.status === 'WAITLISTED') {
    return { label: r.waitlist_position ? `Waitlist #${r.waitlist_position}` : 'Waitlist', tone: 'warn' };
  }
  if (my(ev.ends_at).isBefore(my())) return { label: 'Ended', tone: 'muted' };
  if (ev.registration_mode === 'NONE') return { label: 'Walk in', tone: 'brand' };
  if (!ev.registration_open) return { label: 'Closed', tone: 'muted' };
  if (ev.is_full && !ev.waitlist_enabled) return { label: 'Full', tone: 'bad' };
  if (ev.is_full) return { label: 'Waitlist open', tone: 'warn' };
  if (ev.registration_mode === 'PAID') return { label: `${RM(ev.fee_per_pax).replace('.00', '')} / pax`, tone: 'purple' };
  return { label: 'Open', tone: 'brand' };
};

/** "CIDT 7K3M9 QX2PA" — easier to read out at a desk. */
export const groupTicket = (code) => {
  const c = String(code || '');
  return c.length === 14 ? `${c.slice(0, 4)} ${c.slice(4, 9)} ${c.slice(9)}` : c;
};

/**
 * The event QR on the screen at the door is a link:
 *   https://<cidas>/events/checkin?e=<event uuid>&t=<10 hex>
 * Returns { event_id, token } or null for anything else.
 */
export function parseCheckinQr(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  let params = null;
  try {
    params = new URL(raw, 'https://cidas.invalid').searchParams;
  } catch {
    return null;
  }
  const e = params.get('e');
  const t = params.get('t');
  if (!e || !t) return null;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(e)) return null;
  if (!/^[0-9a-f]{10}$/i.test(t)) return null;
  return { event_id: e, token: t.toUpperCase() };
}

/** First name for "Welcome, …". */
// The whole name, as Home greets the agent: the first word of a Malaysian name is
// as often the surname (LEE MEI LING) as a given name.
export const greetName = (agent) => String(agent?.display_name || agent?.full_name || '').trim() || 'there';
